"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LastModifiedTime = exports.CreatedTime = exports.SetTimeZone = exports.SetLocale = exports.DateTimeFormat = exports.DateTimeParse = exports.IsSame = exports.WeekNum = exports.DateStr = exports.TimeStr = exports.WorkDayDiff = exports.WorkDay = exports.IsAfter = exports.IsBefore = exports.ToNow = exports.FromNow = exports.Now = exports.Today = exports.DatetimeDiff = exports.DatetimeDiffUtil = exports.Dateadd = exports.Weekday = exports.Second = exports.Minute = exports.Hour = exports.Day = exports.Month = exports.Year = exports.getDayjs = void 0;
// TODO(remove eslint disable)
/* eslint-disable @typescript-eslint/naming-convention */
const dayjs_1 = __importStar(require("dayjs"));
const basic_1 = require("./basic");
const types_1 = require("../../types");
const string_1 = require("../../utils/string");
const lexer_1 = require("../../formula_parser/lexer");
const params_count_error_1 = require("../../formula_parser/errors/params_count.error");
const unit_error_1 = require("../../formula_parser/errors/unit.error");
const quarterOfYear_1 = __importDefault(require("dayjs/plugin/quarterOfYear"));
const weekOfYear_1 = __importDefault(require("dayjs/plugin/weekOfYear"));
const weekYear_1 = __importDefault(require("dayjs/plugin/weekYear"));
const dayOfYear_1 = __importDefault(require("dayjs/plugin/dayOfYear"));
const customParseFormat_1 = __importDefault(require("dayjs/plugin/customParseFormat"));
const localizedFormat_1 = __importDefault(require("dayjs/plugin/localizedFormat"));
const isoWeek_1 = __importDefault(require("dayjs/plugin/isoWeek"));
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
// list of supported internationalization (i18n)
require("dayjs/locale/zh");
require("dayjs/locale/zh-cn");
require("dayjs/locale/zh-hk");
require("dayjs/locale/zh-tw");
require("dayjs/locale/fr"); // French
require("dayjs/locale/es"); // Spanish
require("dayjs/locale/pt"); // portuguese
require("dayjs/locale/ru"); // Russian
require("dayjs/locale/ar"); // Arabic
require("dayjs/locale/de"); // german
require("dayjs/locale/ja"); // Japanese
require("dayjs/locale/ko"); // Korean
require("dayjs/locale/hi"); // Hindi
dayjs_1.default.extend(quarterOfYear_1.default);
dayjs_1.default.extend(weekOfYear_1.default);
dayjs_1.default.extend(weekYear_1.default);
dayjs_1.default.extend(dayOfYear_1.default);
dayjs_1.default.extend(customParseFormat_1.default);
dayjs_1.default.extend(localizedFormat_1.default);
dayjs_1.default.extend(utc_1.default);
dayjs_1.default.extend(isoWeek_1.default);
var Units;
(function (Units) {
    Units["milliseconds"] = "milliseconds";
    Units["seconds"] = "seconds";
    Units["minutes"] = "minutes";
    Units["hours"] = "hours";
    Units["days"] = "days";
    Units["weeks"] = "weeks";
    Units["months"] = "months";
    Units["quarters"] = "quarters";
    Units["years"] = "years";
})(Units || (Units = {}));
var WeekdayUnits;
(function (WeekdayUnits) {
    WeekdayUnits[WeekdayUnits["sunday"] = 0] = "sunday";
    WeekdayUnits[WeekdayUnits["monday"] = 1] = "monday";
    WeekdayUnits[WeekdayUnits["tuesday"] = 2] = "tuesday";
    WeekdayUnits[WeekdayUnits["wednesday"] = 3] = "wednesday";
    WeekdayUnits[WeekdayUnits["thursday"] = 4] = "thursday";
    WeekdayUnits[WeekdayUnits["friday"] = 5] = "friday";
    WeekdayUnits[WeekdayUnits["saturday"] = 6] = "saturday";
})(WeekdayUnits || (WeekdayUnits = {}));
const UnitMapBase = new Map([
    [Units.milliseconds, [Units.milliseconds, 'ms']],
    [Units.seconds, [Units.seconds, 's']],
    [Units.minutes, [Units.minutes, 'm']],
    [Units.hours, [Units.hours, 'h']],
    [Units.days, [Units.days, 'd']],
    [Units.weeks, [Units.weeks, 'w']],
    [Units.months, [Units.months, 'M']],
    [Units.quarters, [Units.quarters, 'Q']],
    [Units.years, [Units.years, 'y']],
]);
const UnitMap = new Map();
UnitMapBase.forEach((v, k) => {
    UnitMap.set(v[0], k);
    UnitMap.set(v[1], k);
});
const getPureUnit = (unitStr) => {
    const unit = UnitMap.get(String(unitStr));
    if (!unit) {
        throw new unit_error_1.UnitError(unitStr);
    }
    return UnitMapBase.get(unit)[1];
};
const getDayjs = (timeStamp) => {
    // TODO follow-up and lookup synchronous transformation (the timeStamp of string should not be passed in)
    if (timeStamp == null) {
        throw new basic_1.FormulaBaseError('');
    }
    // If the input parameter is already a dayjs object, return directly
    if ((0, dayjs_1.isDayjs)(timeStamp)) {
        return timeStamp;
    }
    const isString = typeof timeStamp === 'string';
    const isTimeStamp = !isString || !Number.isNaN(Number(timeStamp)); // timestamp format check
    const date = isTimeStamp ? (0, dayjs_1.default)(Number(timeStamp)).locale('en') : (0, dayjs_1.default)((0, string_1.dateStrReplaceCN)(timeStamp)).locale('en');
    // Add a unified legal time check, if it fails, throw an error
    if (!date.isValid()) {
        throw new basic_1.FormulaBaseError('');
    }
    return date;
};
exports.getDayjs = getDayjs;
const getStartOfDay = (timeStamp) => {
    return (0, exports.getDayjs)(timeStamp).hour(0).minute(0).second(0).millisecond(0);
};
// Convert the day of the week and the first letter of the month in the date string to uppercase,
// This is compatible with the localization of dayjs
const formatDateTimeStr = (dateStr) => {
    if (typeof dateStr === 'number') {
        return dateStr;
    }
    const reg = /(mon)|(tue)|(wed)|(thu)|(fri)|(sat)|(sun)|(jan)|(feb)|(mar)|(apr)|(may )|(jun)|(jul)|(aug)|(sep)|(oct)|(nov)|(dec)/g;
    return dateStr.replace(reg, (_, $1) => $1.charAt(0).toUpperCase() + $1.slice(1));
};
const DEFAULT_LOCALE = 'zh-cn'; // default localization language
class DateFunc extends basic_1.FormulaFunc {
}
DateFunc.type = types_1.FormulaFuncType.DateTime;
DateFunc.acceptValueType = new Set([types_1.BasicValueType.DateTime, ...basic_1.FormulaFunc.acceptValueType]);
class Year extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'YEAR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return (0, exports.getDayjs)(params[0].value).year();
    }
}
exports.Year = Year;
class Month extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'MONTH', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return (0, exports.getDayjs)(params[0].value).month() + 1;
    }
}
exports.Month = Month;
class Day extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'DAY', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return (0, exports.getDayjs)(params[0].value).date();
    }
}
exports.Day = Day;
class Hour extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'HOUR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return (0, exports.getDayjs)(params[0].value).hour();
    }
}
exports.Hour = Hour;
class Minute extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'MINUTE', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return (0, exports.getDayjs)(params[0].value).minute();
    }
}
exports.Minute = Minute;
class Second extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'SECOND', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return (0, exports.getDayjs)(params[0].value).second();
    }
}
exports.Second = Second;
class Weekday extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'WEEKDAY', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        if (params[0].value == null) {
            return null;
        }
        const startDayOfWeek = params[1] && params[1].value;
        const day = (0, exports.getDayjs)(params[0].value).day();
        if (String(startDayOfWeek).toLowerCase() === 'monday') {
            return day === 0 ? 6 : day - 1;
        }
        return day;
    }
}
exports.Weekday = Weekday;
class Dateadd extends DateFunc {
    static validateParams(params) {
        if (params.length < 3) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'DATEADD', 3);
        }
        const unit = params[2].token.value.slice(1, -1);
        if (params[2].token.type === lexer_1.TokenType.String && !UnitMap.get(unit)) {
            throw new unit_error_1.UnitError(unit);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(params) {
        const [{ value: date }, { value: count }, { value: unitStr }] = params;
        const unit = getPureUnit(unitStr);
        const day = (0, exports.getDayjs)(date);
        return day.add(Number(count), unit).valueOf();
    }
}
exports.Dateadd = Dateadd;
class DatetimeDiffUtil extends DateFunc {
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static calc(params, float) {
        var _a;
        const [{ value: dateFrom }, { value: dateTo }] = params;
        if (dateFrom == null || dateTo == null) {
            return null;
        }
        const unitStr = ((_a = params[2]) === null || _a === void 0 ? void 0 : _a.value) || Units.days;
        const unit = getPureUnit(unitStr);
        const day1 = (0, exports.getDayjs)(dateFrom);
        const day2 = (0, exports.getDayjs)(dateTo);
        return day1.diff(day2, unit, float);
    }
}
exports.DatetimeDiffUtil = DatetimeDiffUtil;
class DatetimeDiff extends DatetimeDiffUtil {
    static validateParams(params) {
        var _a, _b;
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'DATETIME_DIFF', 2);
        }
        const unit = (_a = params[2]) === null || _a === void 0 ? void 0 : _a.token.value.slice(1, -1);
        if (((_b = params[2]) === null || _b === void 0 ? void 0 : _b.token.type) === lexer_1.TokenType.String && unit && !UnitMap.get(unit)) {
            throw new unit_error_1.UnitError(unit);
        }
    }
    static func(params) {
        const diff = this.calc(params, true);
        return diff;
    }
}
exports.DatetimeDiff = DatetimeDiff;
class Today extends DateFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func() {
        const timeStamp = new Date().setHours(0, 0, 0, 0);
        return timeStamp;
    }
}
exports.Today = Today;
class Now extends DateFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func() {
        return Date.now();
    }
}
exports.Now = Now;
class FromNow extends DatetimeDiffUtil {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'FROMNOW', 2);
        }
    }
    static func(params) {
        const diff = this.calc([{ value: Date.now() }, ...params]);
        return Math.abs(Number(diff));
    }
}
exports.FromNow = FromNow;
/**
  * Here FromNow and ToNow have the same function,
  * Separately written just to remind the text and modify the relevant logic later to be better differentiated
  */
class ToNow extends DatetimeDiffUtil {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'TONOW', 2);
        }
    }
    static func(params) {
        const diff = this.calc([{ value: Date.now() }, ...params]);
        return Math.abs(Number(diff));
    }
}
exports.ToNow = ToNow;
class IsBefore extends DateFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'IS_BEFORE', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        if (params === null || params === void 0 ? void 0 : params.some(v => v.value == null)) {
            return null;
        }
        const date1 = (0, exports.getDayjs)(params[0].value);
        const date2 = (0, exports.getDayjs)(params[1].value);
        return date1.isBefore(date2);
    }
}
exports.IsBefore = IsBefore;
class IsAfter extends DateFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'IS_AFTER', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        if (params === null || params === void 0 ? void 0 : params.some(v => v.value == null)) {
            return null;
        }
        const date1 = (0, exports.getDayjs)(params[0].value);
        const date2 = (0, exports.getDayjs)(params[1].value);
        return date1.isAfter(date2);
    }
}
exports.IsAfter = IsAfter;
class WorkDay extends DateFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'WORKDAY', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(params) {
        let startDate = (0, exports.getDayjs)(params[0].value);
        const startDay = startDate.day();
        // Specify which days the weekend is
        const weekends = [0, 6];
        // number of working days elapsed
        const totalWorkDays = Number(params[1].value);
        if (typeof totalWorkDays !== 'number') {
            return null;
        }
        if (totalWorkDays > 10000000 || Number.isNaN(totalWorkDays)) {
            throw new Error('NaN');
        }
        /**
          * Ideas:
          * Take WORKDAY('2021-10-15',8) as an example, 2021-10-15 is Friday, add 8 days to get 2021-10-23,
          * and check the difference between the two dates through the dayjs.diff method One week, that is to say on 2021-10-23
          * Add 2 days to the base (with two days off a week) to get 2021-10-25
          * At this time, take 2021-10-23 as the start date and the revised 2021-10-25 as the end date to do a diff check,
          * you will find that there is another week, repeat the above process,
          * until the start date and end date are in the same week , and the end date is not in
          * The calculation ends on Saturday and Sunday
          */
        if (weekends.includes(startDay)) {
            const startDayDiff = totalWorkDays >= 0 ? (startDay === 6 ? -1 : -2) : (startDay === 6 ? 2 : 1);
            startDate = startDate.add(startDayDiff, 'day');
        }
        let endDate = startDate.add(totalWorkDays, 'day');
        const getDiffWeek = () => endDate.week() === startDate.week() ? 0 : endDate.startOf('week').diff(startDate.startOf('week'), 'week');
        // handle the case where the end date encounters a weekend
        const skipWeekends = (date) => {
            const dayDiff = totalWorkDays >= 0 ? 2 : -2;
            return weekends.includes(date.day()) ? date.add(dayDiff, 'day') : date;
        };
        let diffWeek = getDiffWeek();
        while (diffWeek != 0) {
            startDate = endDate;
            endDate = endDate.add(diffWeek * 2, 'day');
            diffWeek = getDiffWeek();
        }
        let finalDate = skipWeekends(endDate);
        // holiday list
        const holidayStrList = params.length > 2 ? String(params[2].value).split(',') : [];
        const holidays = holidayStrList.filter(v => v !== 'null').map(v => (0, exports.getDayjs)(v.trim()));
        // calculate holidays
        for (let i = 0; i < holidays.length; i++) {
            const holiday = holidays[i];
            const [_start, _end] = totalWorkDays >= 0 ? [(0, exports.getDayjs)(params[0].value), finalDate] : [finalDate, (0, exports.getDayjs)(params[0].value)];
            if (holiday.isBefore(_start, 'date') || holiday.isSame(totalWorkDays >= 0 ? _start : _end, 'date') || holiday.isAfter(_end, 'date')) {
                continue;
            }
            finalDate = totalWorkDays >= 0 ? finalDate.add(1, 'day') : finalDate.add(-1, 'day');
            finalDate = skipWeekends(finalDate);
        }
        return finalDate.valueOf();
    }
}
exports.WorkDay = WorkDay;
class WorkDayDiff extends DateFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'WORKDAY_DIFF', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        let startDate = getStartOfDay(params[0].value);
        let endDate = getStartOfDay(params[1].value);
        const isMinus = startDate.valueOf() > endDate.valueOf(); // Whether with a negative sign
        isMinus && ([startDate, endDate] = [endDate, startDate]);
        const holidayStrList = params.length > 2 ? String(params[2].value).split(',') : [];
        const holidays = holidayStrList.filter(v => v !== 'null').map(v => (0, exports.getDayjs)(v.trim()));
        const weekends = [0, 6]; // specify which days are the weekends
        const startDay = startDate.day();
        const endDay = endDate.day();
        // for size comparison
        const calcStartDay = startDay === 0 ? 7 : startDay;
        const calcEndDay = endDay === 0 ? 7 : endDay;
        const realStartDay = weekends.includes(startDay) ? 5 : startDay;
        const realEndDay = weekends.includes(endDay) ? 5 : endDay;
        let diffDay = 0;
        if (calcStartDay === calcEndDay) {
            diffDay = weekends.includes(startDay) ? 0 : 1;
        }
        if (calcStartDay < calcEndDay && calcStartDay !== 6) {
            diffDay = realEndDay - realStartDay + 1;
        }
        if (calcStartDay > calcEndDay) {
            diffDay = weekends.includes(startDay) ? 5 + realEndDay - realStartDay : 5 + realEndDay - realStartDay + 1;
        }
        const diffWeekCount = endDate.diff(startDate, 'week');
        let finalCount = diffWeekCount * 5 + diffDay;
        // calculate holidays
        for (let i = 0; i < holidays.length; i++) {
            const holiday = holidays[i];
            const holidayDay = holiday.day();
            if (endDate.isAfter(holiday, 'date') &&
                (startDate.isBefore(holiday, 'date') ||
                    startDate.isSame(holiday, 'date')) &&
                !weekends.includes(holidayDay)) {
                finalCount--;
            }
        }
        return isMinus ? -finalCount : finalCount;
    }
}
exports.WorkDayDiff = WorkDayDiff;
class TimeStr extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'TIMESTR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const date = (0, exports.getDayjs)(params[0].value);
        return date.format('HH:mm:ss');
    }
}
exports.TimeStr = TimeStr;
class DateStr extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'DATESTR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const date = (0, exports.getDayjs)(params[0].value);
        return date.format('YYYY-MM-DD');
    }
}
exports.DateStr = DateStr;
class WeekNum extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'WEEKNUM', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        var _a;
        const currentDate = (0, exports.getDayjs)(params[0].value);
        const startDayOfWeek = String((_a = params[1]) === null || _a === void 0 ? void 0 : _a.value).toLowerCase();
        const startOfWeek = startDayOfWeek === 'undefined' ? 0 : WeekdayUnits[startDayOfWeek];
        if (![0, 1].includes(startOfWeek)) {
            throw new Error('NaN');
        }
        if (currentDate.weekYear() !== currentDate.year() && currentDate.week() === 1) {
            const weekOffset = startOfWeek === 0 ? 1 : (currentDate.day() === 0 ? 0 : 1);
            return currentDate.add(-1, 'week').week() + weekOffset;
        }
        if (currentDate.add(-startOfWeek, 'day').startOf('week').year() !== currentDate.year()) {
            return 1;
        }
        if (startOfWeek === 1 && currentDate.day() === 0) {
            return currentDate.week() - 1;
        }
        return currentDate.week();
    }
}
exports.WeekNum = WeekNum;
class IsSame extends DateFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'IS_SAME', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        var _a;
        const [{ value: dateFrom }, { value: dateTo }] = params;
        const unitStr = ((_a = params[2]) === null || _a === void 0 ? void 0 : _a.value) || 'ms';
        const unit = getPureUnit(unitStr);
        const day1 = (0, exports.getDayjs)(dateFrom);
        const day2 = (0, exports.getDayjs)(dateTo);
        return day1.isSame(day2, unit);
    }
}
exports.IsSame = IsSame;
class DateTimeParse extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'DATETIME_PARSE', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(params) {
        var _a;
        const dateStr = formatDateTimeStr(params[0].value);
        // If it is a timestamp, don't pass this parameter directly, let dayjs convert it by itself
        const formatStr = typeof dateStr === 'number' ? '' : String((_a = params[1]) === null || _a === void 0 ? void 0 : _a.value);
        const length = params.length;
        if (length >= 2) {
            return (0, dayjs_1.default)(dateStr, formatStr).valueOf();
        }
        return (0, dayjs_1.default)(dateStr).valueOf();
    }
}
exports.DateTimeParse = DateTimeParse;
class DateTimeFormat extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'DATETIME_FORMAT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        var _a;
        const date = (0, exports.getDayjs)(params[0].value);
        const formatStr = String(((_a = params[1]) === null || _a === void 0 ? void 0 : _a.value) || 'YYYY-MM-DD HH:mm');
        return date.format(formatStr);
    }
}
exports.DateTimeFormat = DateTimeFormat;
class SetLocale extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'SET_LOCALE', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(params) {
        var _a;
        const localeDate = params[0].value;
        const locale = ((_a = params[1]) === null || _a === void 0 ? void 0 : _a.value) || DEFAULT_LOCALE;
        return (0, exports.getDayjs)(localeDate).locale(locale);
    }
}
exports.SetLocale = SetLocale;
class SetTimeZone extends DateFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'SET_TIMEZONE', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(params) {
        var _a;
        const date = (0, exports.getDayjs)(params[0].value);
        let timezoneOffset = Number((_a = params[1]) === null || _a === void 0 ? void 0 : _a.value);
        if (!timezoneOffset && timezoneOffset !== 0) {
            timezoneOffset = 8; // The default time zone is +8 District —— "Asia/Singapore"
        }
        return date.utcOffset(timezoneOffset);
    }
}
exports.SetTimeZone = SetTimeZone;
class CreatedTime extends DateFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(_params, context) {
        var _a, _b;
        const createdAt = (_b = (_a = context.record) === null || _a === void 0 ? void 0 : _a.recordMeta) === null || _b === void 0 ? void 0 : _b.createdAt;
        return createdAt == null ? null : createdAt;
    }
}
exports.CreatedTime = CreatedTime;
class LastModifiedTime extends DateFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.DateTime;
    }
    static func(params, context) {
        var _a;
        const record = context.record;
        const updatedMap = (_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.fieldUpdatedMap;
        if (!updatedMap) {
            return null;
        }
        const fieldIds = !params.length ? Object.keys(updatedMap) : params.map(param => { var _a; return (_a = param.node.field) === null || _a === void 0 ? void 0 : _a.id; });
        const timestamps = fieldIds.map(fieldId => { var _a; return (_a = updatedMap[fieldId]) === null || _a === void 0 ? void 0 : _a.at; }).filter(v => v);
        return timestamps.length ? Math.max(...timestamps) : null;
    }
}
exports.LastModifiedTime = LastModifiedTime;
//# sourceMappingURL=date_time.js.map